<!-- components/RingChart.vue -->
<template>
  <div class="ring-chart-container" :style="{ width: size + 'px', height: size + 'px' }">
    <canvas :ref="chartId"></canvas>
    <div class="ring-chart-value">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js'
Chart.register(...registerables)

export default {
  props: {
    // 2026-05-20
    // - Modified percentageChange prop to be optional with a default value of 0
    //   as this was creawting errors when prop was over 100, which happnes as some
    //   metrics can have changes greater than 100% (e.g. trips, utilization)
    // The percentage change value (-100 to +100)
    percentageChange: {
      type: [Number, null, undefined], // Allow null/undefined during loading
      required: false, // Better to have a default
      default: 0,
      validator (val) {
        // Allow null/undefined (will use default)
        if (val == null) return true

        // Must be a valid number
        if (typeof val !== 'number' || isNaN(val)) {
          console.warn(`[RingChart] percentageChange should be a number, got ${typeof val}`)
          return false
        }

        return true // Accept any number, we'll clamp it inside the component
      }
    },
    percentageChange2: {
      type: Number,
      required: false, // ← changed
      default: 0, // ← added
      validator: (val) => typeof val === 'number' && !isNaN(val) && val >= -100 && val <= 100
    },
    // Whether lower change is better (for violations, fuel efficiency)
    lowerIsBetter: {
      type: Boolean,
      default: false
    },
    // Size of the chart in pixels
    size: {
      type: Number,
      default: 200
    },
    // Thickness of the ring (percentage of chart size)
    thickness: {
      type: String,
      default: '70%'
    }
  },
  data () {
    return {
      chartId: `ring-chart-${Math.random().toString(36).substr(2, 9)}`,
      chart: null
    }
  },
  computed: {
    // Determine if the change is positive (good) or negative (bad)
    isPositiveChange () {
      if (this.lowerIsBetter) {
        // For metrics where lower is better (violations, fuel efficiency)
        return this.percentageChange <= 0
      } else {
        // For metrics where higher is better (trips, utilization)
        return this.percentageChange >= 0
      }
    },

    // Get the color based on change direction
    ringColor () {
      return this.isPositiveChange ? '#4CAF50' : '#F44336'
    },

    // Calculate the ring value (absolute percentage to display)
    // We want to show the magnitude of change, capped at 100%
    ringValue () {
      return Math.min(100, Math.abs(this.percentageChange))
    },

    // Remaining value for the ring
    remainingValue () {
      return 100 - this.ringValue
    },

    // Icon to show in the center (optional)
    trendIcon () {
      if (this.percentageChange === 0) return 'mdi-minus'
      if (this.isPositiveChange) {
        return this.lowerIsBetter ? 'mdi-arrow-down' : 'mdi-arrow-up'
      } else {
        return this.lowerIsBetter ? 'mdi-arrow-up' : 'mdi-arrow-down'
      }
    }
  },
  mounted () {
    this.createChart()
  },
  watch: {
    ringValue: {
      handler () {
        this.updateChart()
      },
      immediate: true
    },
    ringColor () {
      this.updateChartColor()
    }
  },
  beforeDestroy () {
    if (this.chart) {
      this.chart.destroy()
    }
  },
  methods: {
    createChart () {
      this.chart = new Chart(this.$refs[this.chartId], {
        type: 'doughnut',
        data: {
          datasets: [{
            data: [this.ringValue, this.remainingValue],
            backgroundColor: [this.ringColor, '#E0E0E0'],
            borderWidth: 0,
            cutout: this.thickness,
            borderRadius: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            tooltip: { enabled: false },
            legend: { display: false }
          },
          events: []
        }
      })
    },
    updateChart () {
      if (this.chart) {
        this.chart.data.datasets[0].data = [this.ringValue, this.remainingValue]
        this.chart.update()
      }
    },
    updateChartColor () {
      if (this.chart) {
        this.chart.data.datasets[0].backgroundColor[0] = this.ringColor
        this.chart.update()
      }
    }
  }
}
</script>

<style scoped>
.ring-chart-container {
  position: relative;
  margin: 0 auto;
}

.ring-chart-value {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  width: 100%;
  pointer-events: none;
}
</style>
